'use client';

import { useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { formatCategoryForDisplay } from '@terra/ui-design-system';
import {
  SIGNAL_LIST_EMPTY_GROUP_KEY,
  type GroupedSignal,
  type SignalListGroupCount,
} from '@terra/shared-contracts-signals';
import {
  SignalCategorySchema,
  SignalStageSchema,
  SignalStatusSchema,
} from '@terra/shared-schemas-orchestrator';
import type { SignalsListGrouping } from '../../../store/tab/signalsTabScopedFilterStore';
import { isTraceStatusFilter } from '../../../store/tab/tracesTabScopedFilterStore';
import { useListPageGroupCollapse } from '../../common/useListPageGroupCollapse';
import {
  formatSignalEndpointGroupLabel,
  toFindingSeverity,
} from './signals-list.model';
import {
  buildSignalsTableRows,
  isSignalsGroupHeaderRow,
  resolveSignalsLoadMoreRowIndex,
} from './signals-grouping.model';

const NO_GROUPS: readonly SignalListGroupCount[] = [];

export function useSignalsTableRows({
  signals,
  expandedSignalIds,
  grouping,
  groups,
  hasMore,
  resetKey,
}: {
  signals: readonly GroupedSignal[];
  expandedSignalIds: ReadonlySet<string>;
  grouping: SignalsListGrouping;
  groups: readonly SignalListGroupCount[] | undefined;
  hasMore: boolean;
  resetKey: string;
}) {
  const tLifecycle = useTranslations('designSystem.signals.lifecycle');
  const tExecutionStatus = useTranslations('designSystem.executions.status');
  const tSeverity = useTranslations('home.posture.severity');
  const tGroupBy = useTranslations('findingsTab.groupBy');
  const tStatusSelector = useTranslations('signals.signalStatusSelector');
  const tApplicationAssets = useTranslations('designSystem.applicationAssets');
  const {
    collapsedGroupKeys,
    toggleGroup,
    expandAllGroups,
    collapseGroups,
    areAllCollapsed,
  } = useListPageGroupCollapse({
    resetKey: `${grouping?.field ?? ''}:${resetKey}`,
  });
  const groupField = grouping?.field;
  const serverGroups = groups ?? NO_GROUPS;
  const labelByGroupKey = useMemo(
    () =>
      new Map(
        serverGroups.flatMap((group) => {
          const label = group.endpoint
            ? formatSignalEndpointGroupLabel(group.endpoint)
            : group.label;

          return label ? [[group.key, label] as const] : [];
        }),
      ),
    [serverGroups],
  );
  const getGroupLabel = useCallback(
    (groupKey: string): string => {
      if (groupKey === SIGNAL_LIST_EMPTY_GROUP_KEY) {
        switch (groupField) {
          case 'status':
            return tStatusSelector('unclassified');
          case 'endpoint':
            return tApplicationAssets('applicationLevel');
          default:
            return tGroupBy('emptyValue');
        }
      }

      switch (groupField) {
        case 'status': {
          const status = SignalStatusSchema.safeParse(groupKey);

          return status.success
            ? tLifecycle(`status.${status.data}`)
            : groupKey;
        }

        case 'stage': {
          const stage = SignalStageSchema.safeParse(groupKey);

          return stage.success ? tLifecycle(`stage.${stage.data}`) : groupKey;
        }

        case 'category': {
          const category = SignalCategorySchema.safeParse(groupKey);

          return category.success
            ? formatCategoryForDisplay(category.data)
            : groupKey;
        }

        case 'endpoint':
        case 'asset':
          return labelByGroupKey.get(groupKey) ?? groupKey;
        case 'execution':
          return isTraceStatusFilter(groupKey)
            ? tExecutionStatus(groupKey)
            : groupKey;
        case 'finding': {
          const severity = toFindingSeverity(groupKey);

          return severity ? tSeverity(severity) : groupKey;
        }

        case 'source':
        case undefined:
          return groupKey;
      }
    },
    [
      groupField,
      labelByGroupKey,
      tApplicationAssets,
      tExecutionStatus,
      tGroupBy,
      tLifecycle,
      tSeverity,
      tStatusSelector,
    ],
  );
  const rows = useMemo(
    () =>
      buildSignalsTableRows({
        signals,
        expandedSignalIds,
        grouping,
        groups: serverGroups,
        collapsedGroupKeys,
        getGroupLabel,
      }),
    [
      collapsedGroupKeys,
      expandedSignalIds,
      getGroupLabel,
      grouping,
      serverGroups,
      signals,
    ],
  );
  const groupKeys = useMemo(
    () =>
      rows.flatMap((row) =>
        isSignalsGroupHeaderRow(row) ? [row.groupKey] : [],
      ),
    [rows],
  );

  return {
    rows,
    loadMoreRowIndex: resolveSignalsLoadMoreRowIndex({
      rows,
      grouping,
      collapsedGroupKeys,
      hasServerGroups: groups !== undefined,
      hasMore,
    }),
    collapsedGroupKeys,
    toggleGroup,
    areAllGroupsCollapsed: areAllCollapsed(groupKeys),
    expandAllGroups,
    collapseAllGroups: () => collapseGroups(groupKeys),
  };
}
